// Prop Bank (E4-P5, U4) — sheet 4 view khoá HÌNH DÁNG đồ vật lặp lại. Dựng lại theo thẻ TopView ở đợt 260922 E2-P3.
//
// Vì sao cần ảnh khi đã có mô tả chữ: `canonical` pin được MÀU + CHẤT LIỆU, nhưng "cross-shaped silver
// dagger" mỗi clip vẫn ra một kiểu chuôi khác. Ảnh khoá được hình.
//
// 🔴 AI ĐIỀN → NGƯỜI ĐÈ → PIN: AI xếp hạng ứng viên kèm LÝ DO (mấy event nhắc tới, có shot cận không),
// người TICK cái đáng gen, rồi mới bấm. Không có nút "gen tất cả" — mỗi sheet ăn một slot trong trần
// 9 ref/clip và tốn một lượt image-gen, nên đó phải là quyết định của người.
import React, { useEffect, useState, useCallback } from 'react';
import { Button, message, Tag, Tooltip, Checkbox, Typography, Spin, Modal, Input, Dropdown } from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { filmApi } from '../../services/api/film';
import { useBankErrorReporter } from './WorkflowRunContext'; // P9D #6 (I10)
import { apiErrorText, bankInUse } from './apiErrorText';
import { useFilmPlan } from './FilmPlanContext';
import type { FilmProp, ImageModelInfo, PropCandidate } from '../../types/film';
import ImageComposer from './ImageComposer';
import EpisodeOnlyFilter from './EpisodeOnlyFilter';
import { useBankUsage } from './useBankUsage';
import { filterByEpisode } from './bankFilterUtils';
import type { BankViewProps } from './bankViewProps';
import FilmAssetCard, { type AssetField } from './FilmAssetCard';
import { categoryLabel, mergeAliasList, PROP_CATEGORY_OPTIONS } from './propCardUtils';
import { useBankVars } from './useBankVars';
import { AssetTabHead, AssetAddButton, AssetHeadButton } from './AssetTabHead';
import { ASSET_TAB_HEAD } from './assetCardsUi';
import './bank.css';
import './assetCards.css';

const { Text, Paragraph } = Typography;

type Busy = 'gen' | 'upload' | 'delete' | 'suggest' | 'merge' | null;

/**
 * `board` (UI-assettabs) = khung bước Tài sản: đầu tab + thẻ kiểu bảng, và KHÔNG vẽ (cũng không nạp) khối «Ứng viên từ kịch bản» —
 * nguồn `prop-candidates` (regex) đã lỗi thời với luồng Film: đạo cụ chỉ đến từ khám phá tài sản + thêm tay. Bank cũ (FilmBankTab) không truyền `board` ⇒ giữ nguyên.
 */
/** `onlyIds` (261030 B1): ngăn Sửa của màn Tài sản khung 4 bước chỉ mở đúng đạo cụ này (giữ nguyên nút sửa ảnh). */
const FilmPropBank: React.FC<BankViewProps & { onChanged?: () => void;workflowPlanId?:string; board?: boolean; onlyIds?: string[] }> = ({ episodeOnlyDefault = true, onChanged,workflowPlanId, board, onlyIds }) => {
  const { storyId, planId } = useFilmPlan();
  const reportError = useBankErrorReporter();
  const vars = useBankVars();
  const [props_, setProps] = useState<FilmProp[]>([]);
  const [models, setModels] = useState<ImageModelInfo[]>([]);
  // 260819: lọc theo tập đang mở — đạo cụ khớp theo tên được nhắc trong action/frameNote của tập.
  const { usage,error:usageError,reload:reloadUsage } = useBankUsage(planId,!!workflowPlanId);
  const [epOnly, setEpOnly] = useState(episodeOnlyDefault);
  const [cands, setCands] = useState<PropCandidate[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [studio, setStudio] = useState<FilmProp | null>(null);
  const [genning, setGenning] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, Busy>>({});
  // Thêm tay — thẻ trống đầu lưới. `from` = ứng viên kịch bản đang được thêm (điền sẵn tên + lý do làm mồi cho AI).
  const [adding, setAdding] = useState<{ name: string; desc: string; category?: string; from?: string } | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  // Đạo cụ vừa thêm tay chưa được kịch bản nhắc ⇒ bộ lọc "Chỉ tập này" sẽ giấu nó ngay. Giữ trên màn trong phiên này.
  const [recent, setRecent] = useState<string[]>([]);
  // 260922 E4-P2 — cảnh báo (không chặn) của lượt AI viết mô tả gần nhất trên THẺ, theo id đạo cụ (như `visualWarn` ở FilmLocationBank).
  const [warn, setWarn] = useState<Record<string, string[]>>({});

  const load = useCallback(async () => {
    if (!storyId) return;
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        filmApi.listProps(storyId),
        planId && !board ? filmApi.propCandidates(planId).then((r) => r.candidates) : Promise.resolve([]),
      ]);
      setProps(p); setCands(c);
    } catch (e: any) { message.error(`Load lỗi: ${e?.response?.data?.message || e?.message}`); }
    finally { setLoading(false); }
  }, [storyId, planId, board]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { filmApi.imageStudioModels().then(setModels).catch(() => setModels([])); }, []);

  const changed = () => { load(); onChanged?.(); };
  const run = async (id: string, kind: Busy, fn: () => Promise<unknown>, what: string, ok?: string) => {
    setBusy((b) => ({ ...b, [id]: kind }));
    try { await fn(); if (ok) message.success(ok); changed(); }
    catch (e: any) { reportError(e, what); }
    finally { setBusy((b) => { const n = { ...b }; delete n[id]; return n; }); }
  };

  const toggle = (name: string) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(name)) n.delete(name); else n.add(name);
    return n;
  });

  // Gen TUẦN TỰ và hiện tên cái đang chạy: mỗi sheet là một lượt image-gen, người vận hành cần thấy tiến độ thật.
  const genPicked = async () => {
    const todo = cands.filter((c) => picked.has(c.name));
    if (!todo.length) { message.warning('Chưa tick đạo cụ nào'); return; }
    /**
     * 🔴 Ứng viên bóc từ kịch bản CHƯA có mô tả cố định (màu, chất liệu) — cố ý không bịa, vì bịa sai chất
     * liệu thì ảnh sheet ra sai còn tệ hơn không có. Chặn tại đây thay vì để server trả 400 khó hiểu.
     */
    const thieuMoTa = todo.filter((c) => !String(c.canonical || '').trim());
    if (thieuMoTa.length) {
      message.warning(`Cần mô tả trước khi gen: ${thieuMoTa.map((c) => c.name).join(', ')} — bấm "Thêm vào kho" để nhập.`, 8);
      return;
    }
    for (const c of todo) {
      setGenning(c.name);
      try {
        // Ứng viên chưa có loại ⇒ không gửi `category` (backend dùng 'Prop' cho prompt, đạo cụ mới ⇒ NULL).
        await filmApi.generatePropSheet(storyId, { name: c.name, canonical: c.canonical, planId });
        message.success(`Đã gen sheet: ${c.name}`);
      } catch (e: any) {
        message.error(`${c.name}: ${e?.response?.data?.message || e?.message}`);
        break; // lỗi đầu thường là lỗi chung (safety block / hết quota) — dừng, đừng đốt tiếp
      }
    }
    setGenning(null);
    setPicked(new Set());
    changed();
  };

  /** Xoá: 409 khi đạo cụ đã có ảnh sheet → hỏi lại rồi mới cưỡng chế. */
  const del = async (p: FilmProp, force = false) => {
    setBusy((b) => ({ ...b, [p.id]: 'delete' }));
    try { await filmApi.deleteProp(p.id, force); message.success(`Đã xoá "${p.name}"`); changed(); }
    catch (e: any) {
      const inUse = bankInUse(e);
      if (!inUse) { message.error(apiErrorText(e, 'Xoá đạo cụ')); return; }
      Modal.confirm({
        title: `"${inUse.name}" đang được dùng`,
        content: <div style={{ fontSize: 13 }}>{inUse.usage.map((u) => <div key={u}>• {u}</div>)}<div style={{ marginTop: 8 }}>Vẫn xoá? Không hoàn lại được.</div></div>,
        okText: 'Vẫn xoá', okButtonProps: { danger: true }, cancelText: 'Huỷ',
        onOk: () => del(p, true),
      });
    } finally { setBusy((b) => { const n = { ...b }; delete n[p.id]; return n; }); }
  };

  /** Thêm đạo cụ tay, KHÔNG gen ảnh — chỉ ghi tên + mô tả cố định + loại để prompt nhắc đúng. Gen sheet bấm sau trên thẻ. */
  const addProp = async () => {
    if (!adding?.name.trim()) return;
    try {
      const made = await (workflowPlanId?filmApi.createWorkflowProp(workflowPlanId,{name:adding.name.trim(),description:adding.desc.trim()||undefined,category:adding.category||undefined}):filmApi.createProp(storyId,{name:adding.name.trim(),description:adding.desc.trim()||undefined,category:adding.category||undefined}));
      setRecent((r) => [made.id, ...r.filter((x) => x !== made.id)]);
      message.success(`Đã thêm "${adding.name.trim()}"`);
      setAdding(null); changed();
    } catch (e: any) { message.error(apiErrorText(e, 'Thêm đạo cụ')); }
  };

  /** AI đọc bối cảnh/thời đại của truyện rồi viết loại + mô tả — người xem lại và sửa trước khi gen ảnh. */
  const suggestFor = async (name: string, hint?: string) => {
    return filmApi.suggestPropCanonical(storyId, { name, hint, planId });
  };

  const saveField = async (p: FilmProp, field: AssetField, value: string) => {
    if (field === 'spatialLayout') return;
    try { await filmApi.updateProp(p.id, { [field]: value }); changed(); }
    catch (e: any) { message.error(apiErrorText(e, 'Lưu đạo cụ')); }
  };

  /**
   * Gen sheet cho một đạo cụ ĐÃ nằm trong Bank (endpoint ghi đè theo tên, không tạo bản trùng).
   * 🔴 Nút này phải có: `fresh` lọc ứng viên theo `hasSheet` (chỉ xét TÊN) ⇒ đạo cụ thêm tay biến mất khỏi ứng viên
   * ngay, không còn đường nào gen ảnh (ngõ cụt 260814).
   */
  const genFor = (p: FilmProp) => {
    const canonical = (p.description || '').trim();
    if (!canonical) { message.warning('Cần mô tả (màu, chất liệu) trước khi gen — bấm vào ô mô tả để nhập.'); return Promise.resolve(); }
    return run(p.id, 'gen', () => filmApi.generatePropSheet(storyId, { name: p.name, canonical, category: p.category || undefined, planId }), `Gen sheet "${p.name}"`, `Đã gen sheet: ${p.name}`);
  };

  const genWithModel = (p: FilmProp, model?: string) => run(p.id, 'gen', async () => {
    const r = await filmApi.imageStudioGenerate('PROP', p.id, { model, planId });
    await filmApi.imageStudioChoose(r.variant.id, planId);
    (r.warnings || []).forEach((w) => message.warning(w, 8));
  }, 'Gen ảnh đạo cụ', 'Đã gen và dùng ảnh mới');

  /**
   * 260922 E4-P3 — gộp ứng viên khớp DANH TỪ CHÍNH + CHỦ ("HEAD") vào một đạo cụ Bank đã có, thành tên phụ.
   * Chỉ GỢI Ý — người bấm mới ghi (User 15/09: kiếm Marcus và song kiếm Livia khớp cùng "sword" nhưng LÀ HAI
   * VẬT, không được tự động gộp).
   *
   * 🔴 Lấy bản ĐẦY ĐỦ, MỚI NHẤT của X từ state `props_` (không dùng thẳng `target` từ response `prop-candidates`,
   * có thể đã cũ) — phản biện lượt 1 blocker: thiếu bước này thì gộp liên tiếp hai ứng viên vào cùng X sẽ
   * LÀM MẤT tên gộp trước đó (state cũ không có tên vừa thêm).
   */
  const mergeIntoBank = (candidateName: string, target: { id: string; name: string; aliases: string[] }) => {
    const freshTarget = props_.find((pp) => pp.id === target.id) || target;
    const aliases = mergeAliasList(freshTarget, candidateName);
    return run(target.id, 'merge', () => filmApi.updateProp(target.id, { aliases }),
      `Gộp vào "${freshTarget.name}"`, `Đã gộp "${candidateName}" vào "${freshTarget.name}"`);
  };

  const fresh = cands.filter((c) => !c.hasSheet);
  const shownProps = onlyIds ? props_.filter((p) => onlyIds.includes(p.id)) : [
    ...props_.filter((p) => recent.includes(p.id)),
    ...filterByEpisode(props_, usage?.propIds, { on: epOnly, filterable: !!usage?.filterable.props }).filter((p) => !recent.includes(p.id)),
  ];

  if(workflowPlanId&&usageError)return <div role="alert">{usageError}<button type="button" onClick={reloadUsage}>Tải lại</button></div>;
  return (
    <div style={vars} className={board ? 'ac-scope' : undefined}>
      {onlyIds ? null : board ? (
        <AssetTabHead title={ASSET_TAB_HEAD.props.title} sub={ASSET_TAB_HEAD.props.sub} onRefresh={changed} refreshing={loading} testId="ac-head-props">
          <EpisodeOnlyFilter board on={epOnly} onChange={setEpOnly} shown={shownProps.length} total={props_.length}
            filterable={!!usage?.filterable.props} />
          <AssetAddButton onClick={() => setAdding({ name: '', desc: '' })}>Thêm đạo cụ</AssetAddButton>
        </AssetTabHead>
      ) : (
      <div className="bk-head">
        <h3>Đạo cụ</h3>
        <span className="sub">chỉ món khán giả nhìn thấy nhiều lần hoặc thấy cận</span>
        <span className="sp" />
        <EpisodeOnlyFilter on={epOnly} onChange={setEpOnly} shown={shownProps.length} total={props_.length}
          filterable={!!usage?.filterable.props} />
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setAdding({ name: '', desc: '' })}>Thêm đạo cụ</Button>
        <Button size="small" icon={<ReloadOutlined />} onClick={changed} loading={loading} />
      </div>
      )}

      {/* ── Ứng viên AI xếp hạng từ kịch bản ── */}
      {planId && !board && fresh.length > 0 && (
        <div className="bk-cands">
          <div className="bk-lbl" style={{ marginBottom: 6 }}>Ứng viên từ kịch bản <span className="bk-cnt">{fresh.length}</span></div>
          {fresh.map((c) => (
            <div className="bk-cand" key={c.name}>
              <Checkbox checked={picked.has(c.name)} onChange={() => toggle(c.name)} disabled={!!genning} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <Text strong style={{ fontSize: 12.5 }}>{c.name}</Text>
                  {c.fromScript && (
                    <Tooltip title="Bóc thẳng từ hành động trong kịch bản — Element Bible không có món này">
                      <Tag color="purple" style={{ fontSize: 10, marginInlineEnd: 0 }}>từ kịch bản</Tag>
                    </Tooltip>
                  )}
                  <Tooltip title="Vì sao được đề xuất: xuất hiện ở nhiều event thì khán giả có cơ hội so sánh; có shot cận thì sai hình lộ ngay">
                    <Tag color={c.closeShots > 0 ? 'orange' : 'default'} style={{ fontSize: 10, marginInlineEnd: 0 }}>{c.reason}</Tag>
                  </Tooltip>
                  {c.setPiece && (
                    <Tooltip title="Cảnh ngồi trong xe / quanh bàn cần ảnh bối cảnh; sheet 4 góc nền trắng chỉ hợp khi nhìn nó từ ngoài như một đồ vật">
                      <Tag color="default" style={{ fontSize: 10, marginInlineEnd: 0 }}>nên là bối cảnh</Tag>
                    </Tooltip>
                  )}
                  {genning === c.name && <Spin size="small" />}
                </div>
                {c.canonical ? (
                  <Paragraph type="secondary" style={{ fontSize: 11, margin: '2px 0 0' }} ellipsis={{ rows: 2, tooltip: c.canonical }}>{c.canonical}</Paragraph>
                ) : (
                  <div style={{ margin: '2px 0 0' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Chưa có mô tả cố định (màu, chất liệu) — cần trước khi gen ảnh.</Text>
                    <Button size="small" type="link" style={{ padding: '0 6px', fontSize: 11 }}
                      onClick={() => setAdding({ name: c.name, desc: '', from: c.reason })}>Thêm vào kho</Button>
                  </div>
                )}
                {c.bankMatch?.how === 'HEAD' && (
                  <div style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Có thể là món đã có:</Text>
                    {c.bankMatch.props.map((x) => (
                      <Button key={x.id} size="small" disabled={busy[x.id] === 'merge'} loading={busy[x.id] === 'merge'}
                        onClick={() => mergeIntoBank(c.name, x)}>
                        Gộp vào «{x.name}»
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <Button type="primary" size="small" style={{ marginTop: 8 }} disabled={!picked.size || !!genning} loading={!!genning} onClick={genPicked}>
            Gen sheet cho {picked.size || 0} món đã tick
          </Button>
        </div>
      )}

      <div className={board ? 'ac-grid' : 'bk-grid'}>
        {adding && (
          <div className={board ? 'ac-card' : 'bk-card'}>
            <div className={board ? 'ac-add' : 'bk-inl'} style={board ? undefined : { margin: 12 }}>
              <div className="hd"><b>{adding.from ? `Thêm "${adding.name}" vào kho` : 'Đạo cụ mới'}</b></div>
              {adding.from && <div className="bk-dimline" style={{ marginTop: 0 }}>Bóc từ kịch bản — {adding.from}</div>}
              <Input size="small" autoFocus placeholder="Tên, vd cây gậy chống đầu bạc" value={adding.name}
                onChange={(e) => setAdding({ ...adding, name: e.target.value })} />
              <Input.TextArea size="small" autoSize={{ minRows: 2, maxRows: 6 }}
                placeholder="Mô tả hình dáng — màu + chất liệu, đừng tả hành động" value={adding.desc}
                onChange={(e) => setAdding({ ...adding, desc: e.target.value })} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Loại</Text>
                <Dropdown trigger={['click']} menu={{
                  items: [...PROP_CATEGORY_OPTIONS.map((c) => ({ key: c, label: categoryLabel(c) })), { type: 'divider' as const }, { key: '', label: 'Bỏ trống' }],
                  onClick: ({ key }) => setAdding((a) => (a ? { ...a, category: String(key) || undefined } : a)),
                }}>
                  <button type="button" className={`bk-chip ${adding.category ? 'ok' : ''}`}>{categoryLabel(adding.category)}</button>
                </Dropdown>
              </div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button size="small" loading={suggesting} disabled={!adding.name.trim()} onClick={async () => {
                  setSuggesting(true);
                  try {
                    const r = await suggestFor(adding.name.trim(), adding.from);
                    setAdding((a) => (a ? { ...a, desc: r.canonical, category: r.category || a.category } : a));
                  } catch (e: any) { reportError(e, 'Nhờ AI viết mô tả'); }
                  finally { setSuggesting(false); }
                }}>✨ AI viết mô tả</Button>
                <Button size="small" onClick={() => setAdding(null)}>Thôi</Button>
                <Button size="small" type="primary" disabled={!adding.name.trim()} onClick={addProp}>Thêm</Button>
              </div>
            </div>
          </div>
        )}
        {shownProps.map((p) => (
          <FilmAssetCard key={p.id} board={board} kind="prop" item={p} models={models} busy={busy[p.id] || null}
            onSaveField={(f, v) => saveField(p, f, v)}
            onGen={() => genFor(p)}
            onGenWithModel={(m) => genWithModel(p, m)}
            onUpload={(file) => run(p.id, 'upload', () => filmApi.imageStudioUpload('PROP', p.id, file, planId), 'Tải ảnh lên', 'Đã dùng ảnh tải lên')}
            onOpenStudio={() => setStudio(p)}
            onDelete={() => del(p)}
            onSuggestDescription={() => run(p.id, 'suggest', async () => {
              const r = await suggestFor(p.name);
              await filmApi.updateProp(p.id, { description: r.canonical, category: r.category || undefined });
              setWarn((w) => ({ ...w, [p.id]: r.warnings || [] }));
            }, 'Nhờ AI viết mô tả', 'AI đã viết mô tả — xem lại rồi sửa nếu cần')}
            onSaveAliases={async (list) => { await filmApi.updateProp(p.id, { aliases: list }); changed(); }}
            visualWarnings={warn[p.id]} />
        ))}
      </div>
      {!shownProps.length && !adding && (
        <div className="bk-dimline" style={{ padding: '24px 0', textAlign: 'center' }}>
          {props_.length ? `Tập này không dùng đạo cụ nào trong ${props_.length} món của truyện.` : 'Chưa có đạo cụ nào.'}
        </div>
      )}

      {studio && (
        <ImageComposer open={!!studio} backend={{ kind: 'studio', ownerType: 'PROP', ownerId: studio.id }} planId={planId}
          title={studio.name} onClose={() => setStudio(null)} onChanged={changed}
          bankImages={props_.filter((x) => x.canonicalRefUrl && x.id !== studio.id).map((x) => ({ key: x.id, url: x.canonicalRefUrl!, label: x.name }))} />
      )}
    </div>
  );
};

export default FilmPropBank;
